// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// useMsalConvexAuth — bridges the app's existing Microsoft (MSAL/Azure AD)
// sign-in to Convex's auth (#191 / #201, the "login lock").
//
// Convex's <ConvexProviderWithAuth useAuth={...}> expects a hook returning
// { isLoading, isAuthenticated, fetchAccessToken }. We hand Convex the Azure AD
// **ID token** (its iss/aud match convex/auth.config.ts), acquired silently from
// the already-signed-in MSAL account. Convex calls fetchAccessToken on connect
// and on refresh; we honor forceRefreshToken.
//
// Only mounted when Convex is configured (CONVEX_CONFIGURED) — i.e. for
// convexOnly/both users on a deployment that has REACT_APP_CONVEX_URL. On UAT
// today (no URL, everyone mongoOnly) this never runs.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

// Same app-registration scope the app already uses (acquireTokenSilent returns
// the ID token alongside the access token in one call).
const LOGIN_SCOPES = ["4ae8c1ac-41df-46bb-a881-011a10d1b09b/.default"];

export function useMsalConvexAuth() {
  const { instance, accounts, inProgress } = useMsal();

  const isAuthenticated = accounts && accounts.length > 0;
  const isLoading = inProgress !== InteractionStatus.None && !isAuthenticated;

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken } = {}) => {
      const account = instance.getActiveAccount() || (accounts && accounts[0]);
      if (!account) return null;
      try {
        const result = await instance.acquireTokenSilent({
          scopes: LOGIN_SCOPES,
          account,
          forceRefresh: !!forceRefreshToken,
        });
        // Convex validates the ID token (iss/aud per convex/auth.config.ts).
        return result.idToken ?? null;
      } catch (err) {
        console.log(
          "%cuseMsalConvexAuth: silent token failed",
          "color:#f87171;font-weight:bold;",
          err
        );
        return null;
      }
    },
    [instance, accounts]
  );

  return useMemo(
    () => ({ isLoading, isAuthenticated, fetchAccessToken }),
    [isLoading, isAuthenticated, fetchAccessToken]
  );
}
